import { extendTheme } from "@mui/joy/styles";

declare module "@mui/joy/styles" {
  // No custom tokens found, you can skip the theme augmentation.
}

const theme = extendTheme({
  colorSchemes: {
    dark: {
      palette: {
        primary: {
          50: "#f0f9ff",
          100: "#e0f2fe",
          200: "#bae6fd",
          300: "#7dd3fc",
          400: "#38bdf8",
          500: "#0ea5e9",
          600: "#0284c7",
          700: "#0369a1",
          800: "#075985",
          900: "#0c4a6e",
        },
        background: {
          body: "#0a0a0a",
          surface: "rgba(0, 0, 0, 0.4)",
          popup: "rgba(0, 0, 0, 0.8)",
        },
        text: {
          primary: "#ffffff",
          secondary: "rgba(255, 255, 255, 0.7)",
          tertiary: "rgba(255, 255, 255, 0.5)",
        },
      },
    },
    light: {
      palette: {
        primary: {
          50: "#f8fafc",
          100: "#f1f5f9",
          200: "#e2e8f0",
          300: "#cbd5e1",
          400: "#94a3b8",
          500: "#64748b",
          600: "#475569",
          700: "#334155",
          800: "#1e293b",
          900: "#0f172a",
        },
        background: {
          body: "#ffffff",
          surface: "rgba(255, 255, 255, 0.9)",
          popup: "rgba(255, 255, 255, 0.95)",
        },
        text: {
          primary: "#0f172a",
          secondary: "rgba(15, 23, 42, 0.7)",
          tertiary: "rgba(15, 23, 42, 0.5)",
        },
      },
    },
  },
  typography: {
    h1: {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(3rem, 8vw, 7rem)",
      fontWeight: "300",
      lineHeight: 1.1,
      letterSpacing: "0.1em",
      textShadow: "0 0 30px rgba(120, 119, 198, 0.3)",
    },
    h2: {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(2rem, 5vw, 4.5rem)",
      fontWeight: "300",
      lineHeight: 1.2,
      letterSpacing: "0.05em",
    },
    h3: {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(1.5rem, 4vw, 3rem)",
      fontWeight: "300",
      lineHeight: 1.3,
      letterSpacing: "0.05em",
    },
    h4: {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(1.25rem, 3vw, 2.25rem)",
      fontWeight: "300",
      lineHeight: 1.4,
      letterSpacing: "0.02em",
    },
    "title-lg": {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(1.125rem, 2.5vw, 1.875rem)",
      fontWeight: "300",
      lineHeight: 1.4,
      letterSpacing: "0.02em",
    },
    "title-md": {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(1rem, 2vw, 1.5rem)",
      fontWeight: "300",
      lineHeight: 1.5,
      letterSpacing: "0.02em",
    },
    "title-sm": {
      fontFamily: "'Cinzel', serif",
      fontSize: "clamp(0.875rem, 1.5vw, 1.125rem)",
      fontWeight: "400",
      lineHeight: 1.5,
      letterSpacing: "0.02em",
    },
    "body-lg": {
      fontFamily: "'EB Garamond', serif",
      fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
      lineHeight: 1.7,
      letterSpacing: "0.01em",
    },
    "body-md": {
      fontFamily: "'EB Garamond', serif",
      fontSize: "clamp(0.875rem, 1.25vw, 1.125rem)",
      lineHeight: 1.6,
      letterSpacing: "0.01em",
    },
    "body-sm": {
      fontFamily: "'EB Garamond', serif",
      fontSize: "clamp(0.75rem, 1vw, 1rem)",
      lineHeight: 1.6,
      letterSpacing: "0.01em",
    },
    "body-xs": {
      fontFamily: "'EB Garamond', serif",
      fontSize: "clamp(0.625rem, 0.875vw, 0.875rem)",
      lineHeight: 1.5,
      letterSpacing: "0.01em",
    },
  },
  components: {
    JoyCard: {
      styleOverrides: {
        root: {
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        },
      },
    },
    JoyButton: {
      styleOverrides: {
        root: {
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontFamily: "'Cinzel', serif",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          },
        },
      },
    },
    JoyChip: {
      styleOverrides: {
        root: {
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
      },
    },
  },
  focus: {
    default: {
      outlineWidth: "2px",
      outlineColor: "rgba(120, 119, 198, 0.6)",
    },
  },
  shadow: {
    xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  },
});

export default theme;
